/* eslint-disable react/no-multi-comp */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isNeedShowElectronOnlyElements } from '../../../../base/environment/utils_web';
import { setAutoGainControl } from '../../../../base/conference/actions.web';
import { isAGCDisabled } from '../../../../base/settings/functions.any';
import { IconMic, IconModeratorSettings, IconVolumeUp } from '../../../../base/icons/svg';
import JitsiMeetJS from '../../../../base/lib-jitsi-meet';
import { equals } from '../../../../base/redux/functions';
import Checkbox from '../../../../base/ui/components/web/Checkbox';
import ContextMenu from '../../../../base/ui/components/web/ContextMenu';
import ContextMenuItem from '../../../../base/ui/components/web/ContextMenuItem';
import ContextMenuItemGroup from '../../../../base/ui/components/web/ContextMenuItemGroup';
import { isLinux, isWindows } from '../../../../base/environment/environment';
import { toggleNoiseSuppression } from '../../../../noise-suppression/actions';
import { isNoiseSuppressionEnabled } from '../../../../noise-suppression/functions';
import { isPrejoinPageVisible } from '../../../../prejoin/functions';
import { createLocalAudioTracks } from '../../../functions.web';
import { showNotification } from '../../../../notifications/actions';
import { NOTIFICATION_ICON, NOTIFICATION_TIMEOUT_TYPE } from '../../../../notifications/constants';
import MicrophoneEntry from './MicrophoneEntry';
import SpeakerEntry from './SpeakerEntry';
const browser = JitsiMeetJS.util.browser;
/**
 * Translates the default device label into a more user friendly one.
 *
 * @param {string} deviceId - The device Id.
 * @param {string} label - The device label.
 * @param {Function} t - The translation function.
 * @returns {string}
 */
function transformDefaultDeviceLabel(deviceId, label, t) {
    return deviceId === 'default'
        ? t('settings.sameAsSystem', { label: label.replace('Default - ', '') })
        : label;
}
const useStyles = makeStyles()(theme => {
    return {
        contextMenu: {
            width: '362px',
            position: 'relative',
            right: 'auto',
            margin: 0,
            marginBottom: '10px',
            maxHeight: 'calc(100dvh - 100px)',
            overflow: 'auto',
        },
        header: {
            '& .context-text-item': {
                fontFamily: 'Lato Bold',
                fontWeight: 'normal',
            },
            '&:hover': {
                backgroundColor: 'initial',
                cursor: 'initial',
                '& .context-text-item': {
                    color: 'rgba(255, 255, 255, 0.75)',
                }
            }
        },
        list: {
            margin: 0,
            padding: 0,
            listStyleType: 'none'
        },
        checkboxContainer: {
            paddingLeft: '19px',
        },
        openMicroSystemSettings: {
            paddingLeft: '11px',
        },
        checkbox: {
            padding: '9px 0 8px 4px',
            width: '100%',
            '.checkbox-text': {
                lineHeight: '17px',
            },
            '&:hover': {
                borderRadius: '0 !important',
                '.checkbox-text': {
                    color: 'rgba(255, 255, 255, 1)',
                },
            }
        },
        separateLineContainer: {
            paddingTop: '6px',
            paddingBottom: '7px',
            paddingLeft: '24px',
            paddingRight: '24px'
        },
        separateLine: {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            height: '1px'
        },
        visitorContainer: {
            padding: "6px 24px",
        },
        visitorContent: {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderRadius: "5px",
            padding: "12px",
            fontFamily: 'Lato Regular',
            fontWeight: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            color: 'rgba(255, 255, 255, 0.75)',
            userSelect: 'none',
            letterSpacing: '-0.15px',
        }
    };
});
const AudioSettingsContent = ({ currentMicDeviceId, currentOutputDeviceId, measureAudioLevels, microphoneDevices, noiseSuppressionEnabled, isAutoGainControlDisabled, changeAutoGainControl, outputDevices, prejoinVisible, setAudioInputDevice, setAudioOutputDevice, toggleSuppression, isVisitorButton, }) => {
    const _componentWasUnmounted = useRef(false);
    const microphoneHeaderId = 'microphone_settings_header';
    const speakerHeaderId = 'speaker_settings_header';
    const { classes } = useStyles();
    const [audioTracks, setAudioTracks] = useState(microphoneDevices.map(({ deviceId, label }) => {
        return {
            deviceId,
            hasError: false,
            jitsiTrack: null,
            label
        };
    }));
    const microphoneDevicesRef = useRef(microphoneDevices);
    const { t } = useTranslation();
    const isWindowsOrLinux = isWindows() || isLinux();
    const openRecordingSystemSettingsHandler = () => {
        window.open('ms-settings:sound');
    };
    /**
     * Click handler for the microphone entries.
     *
     * @param {string} deviceId - The deviceId for the clicked microphone.
     * @returns {void}
     */
    const _onMicrophoneEntryClick = useCallback((deviceId) => {
        setAudioInputDevice(deviceId);
    }, [setAudioInputDevice]);
    /**
     * Click handler for the speaker entries.
     *
     * @param {string} deviceId - The deviceId for the clicked speaker.
     * @returns {void}
     */
    const _onSpeakerEntryClick = useCallback((deviceId) => {
        setAudioOutputDevice(deviceId);
    }, [setAudioOutputDevice]);
    /**
     * Renders a single microphone entry.
     *
     * @param {Object} data - An object with the deviceId, jitsiTrack & label of the microphone.
     * @param {number} index - The index of the element, used for creating a key.
     * @param {length} length - The length of the microphone list.
     * @returns {React$Node}
     */
    const _renderMicrophoneEntry = (data, index, length) => {
        const { deviceId, jitsiTrack, hasError } = data;
        const label = transformDefaultDeviceLabel(deviceId, data.label, t);
        const isSelected = deviceId === currentMicDeviceId;
        return (React.createElement(MicrophoneEntry, { deviceId: deviceId, hasError: hasError, index: index, isSelected: isSelected, jitsiTrack: jitsiTrack, key: `me-${index}`, length: length, measureAudioLevels: measureAudioLevels, onClick: _onMicrophoneEntryClick }, label));
    };
    /**
     * Renders a single speaker entry.
     *
     * @param {Object} data - An object with the deviceId and label of the speaker.
     * @param {number} index - The index of the element, used for creating a key.
     * @param {length} length - The length of the speaker list.
     * @returns {React$Node}
     */
    const _renderSpeakerEntry = (data, index, length) => {
        const { deviceId } = data;
        const label = transformDefaultDeviceLabel(deviceId, data.label, t);
        const key = `se-${index}`;
        const isSelected = deviceId === currentOutputDeviceId;
        return (React.createElement(SpeakerEntry, { deviceId: deviceId, index: index, isSelected: isSelected, key: key, length: length, onClick: _onSpeakerEntryClick }, label));
    };
    /**
     * Disposes the audio tracks.
     *
     * @param {Object} tracks - The object holding the audio tracks.
     * @returns {void}
     */
    const _disposeTracks = (tracks) => {
        tracks.forEach(({ jitsiTrack }) => {
            jitsiTrack?.dispose();
        });
    };
    /**
     * Creates and updates the audio tracks.
     *
     * @returns {void}
     */
    const _setTracks = async () => {
        if (browser.isWebKitBased()) {
            // It appears that at the time of this writing, creating audio tracks blocks the browser's main thread for
            // long time on safari. Wasn't able to confirm which part of track creation does the blocking exactly, but
            // not creating the tracks seems to help and makes the UI much more responsive.
            return;
        }
        _disposeTracks(audioTracks);
        const newAudioTracks = await createLocalAudioTracks(microphoneDevices, 5000);
        if (_componentWasUnmounted.current) {
            _disposeTracks(newAudioTracks);
        }
        else {
            setAudioTracks(newAudioTracks);
        }
    };
    useEffect(() => {
        _setTracks();
        return () => {
            _componentWasUnmounted.current = true;
            _disposeTracks(audioTracks);
        };
    }, []);
    useEffect(() => {
        if (!equals(microphoneDevices, microphoneDevicesRef.current)) {
            _setTracks();
            microphoneDevicesRef.current = microphoneDevices;
        }
    }, [microphoneDevices]);
    return (React.createElement(ContextMenu, { activateFocusTrap: true, "aria-labelledby": 'audio-settings-button', className: classes.contextMenu, hidden: false, id: 'audio-settings-dialog', tabIndex: -1 },
        React.createElement(ContextMenuItemGroup, null,
            React.createElement(ContextMenuItem, { accessibilityLabel: isVisitorButton ? t('visitors.audioSettings.title') : t('settings.microphones'), className: classes.header, icon: IconMic, id: microphoneHeaderId, text: isVisitorButton ? t('visitors.audioSettings.title') : t('settings.microphones') }),
            isVisitorButton ? (React.createElement("div", { className: classes.visitorContainer },
                React.createElement("div", { className: classes.visitorContent }, t('visitors.audioSettings.desc')))) : (React.createElement("ul", { "aria-labelledby": microphoneHeaderId, className: classes.list, role: 'radiogroup', tabIndex: -1 }, audioTracks.map((data, i) => _renderMicrophoneEntry(data, i, audioTracks.length))))),
        outputDevices.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.separateLineContainer },
                React.createElement("div", { className: classes.separateLine })),
            React.createElement(ContextMenuItemGroup, null,
                React.createElement(ContextMenuItem, { accessibilityLabel: t('settings.speakers'), className: classes.header, icon: IconVolumeUp, id: speakerHeaderId, text: t('settings.speakers') }),
                React.createElement("ul", { "aria-labelledby": speakerHeaderId, className: classes.list, role: 'radiogroup', tabIndex: -1 }, outputDevices.map((data, i) => _renderSpeakerEntry(data, i, outputDevices.length)))),
            isNeedShowElectronOnlyElements() && isWindowsOrLinux && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classes.separateLineContainer },
                    React.createElement("div", { className: classes.separateLine })),
                React.createElement("div", { className: classes.checkboxContainer, onClick: (e) => e.stopPropagation() },
                    React.createElement(Checkbox, { className: classes.checkbox, classNameText: 'checkbox-text', checked: !isAutoGainControlDisabled, label: t('settings.autoGainControl.checkbox'), onChange: () => changeAutoGainControl(isAutoGainControlDisabled) })),
                isWindows() && (React.createElement(ContextMenuItem, { className: classes.openMicroSystemSettings, accessibilityLabel: t('settings.autoGainControl.systemSettings'), icon: IconModeratorSettings, onClick: openRecordingSystemSettingsHandler, text: t('settings.autoGainControl.systemSettings') }))))))));
};
const mapStateToProps = (state) => {
    return {
        noiseSuppressionEnabled: isNoiseSuppressionEnabled(state),
        prejoinVisible: isPrejoinPageVisible(state),
        isAutoGainControlDisabled: !!isAGCDisabled(state),
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        toggleSuppression() {
            dispatch(toggleNoiseSuppression());
        },
        changeAutoGainControl(currentState) {
            dispatch(showNotification({
                titleKey: currentState
                    ? 'settings.autoGainControl.notificationAlert.titleEnabled'
                    : 'settings.autoGainControl.notificationAlert.titleDisabled',
                descriptionKey: 'settings.autoGainControl.notificationAlert.description',
                appearance: NOTIFICATION_TIMEOUT_TYPE.SHORT,
                icon: NOTIFICATION_ICON.MICROPHONE,
            }));
            dispatch(setAutoGainControl(!currentState));
        },
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(AudioSettingsContent);
